'use client'

import {useEffect, useLayoutEffect, useRef, useState} from 'react'
import {LogIn, RefreshCw, User} from 'lucide-react'
import type {AccountItem} from '@/hooks/useAccounts'
import {avatarDisplaySrc} from '@/lib/utils'

type AccountSwitcherPopoverProps = {
    anchorRect: DOMRect | null
    accounts: AccountItem[]
    accountUnreadCounts: Record<string, number>
    currentWxid: string | undefined
    onSwitch: (wxid: string) => void
    onRefreshProfile: () => Promise<void> | void
    onManageAccounts: () => void
    onClose: () => void
}

const POPOVER_WIDTH = 260
const POPOVER_OFFSET = 8

export function AccountSwitcherPopover({
    anchorRect,
    accounts,
    accountUnreadCounts,
    currentWxid,
    onSwitch,
    onRefreshProfile,
    onManageAccounts,
    onClose,
}: AccountSwitcherPopoverProps) {
    const wrapperRef = useRef<HTMLDivElement | null>(null)
    const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
    const [refreshing, setRefreshing] = useState(false)

    async function handleRefresh() {
        setRefreshing(true)
        try {
            await onRefreshProfile()
        } finally {
            setRefreshing(false)
        }
    }

    useLayoutEffect(() => {
        if (!anchorRect) {
            setPosition(null)
            return
        }
        const popoverHeight = wrapperRef.current?.offsetHeight ?? 360
        const viewportHeight = window.innerHeight
        const viewportWidth = window.innerWidth
        let top = anchorRect.bottom + POPOVER_OFFSET
        if (top + popoverHeight > viewportHeight - 8) {
            top = Math.max(8, anchorRect.top - popoverHeight - POPOVER_OFFSET)
        }
        let left = anchorRect.left + anchorRect.width / 2 - POPOVER_WIDTH / 2
        left = Math.max(8, Math.min(left, viewportWidth - POPOVER_WIDTH - 8))
        setPosition({top, left})
    }, [anchorRect])

    useEffect(() => {
        function onKey(event: KeyboardEvent) {
            if (event.key === 'Escape') onClose()
        }

        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [onClose])

    useEffect(() => {
        function onDocumentMouseDown(event: globalThis.MouseEvent) {
            if (wrapperRef.current && wrapperRef.current.contains(event.target as Node)) return
            onClose()
        }

        function onScroll() {
            onClose()
        }

        document.addEventListener('mousedown', onDocumentMouseDown)
        window.addEventListener('scroll', onScroll, true)
        window.addEventListener('resize', onScroll)
        return () => {
            document.removeEventListener('mousedown', onDocumentMouseDown)
            window.removeEventListener('scroll', onScroll, true)
            window.removeEventListener('resize', onScroll)
        }
    }, [onClose])

    const onlineAccounts = accounts.filter((a) => a.status === 'online')
    const otherOnline = onlineAccounts.filter((a) => a.wxid !== currentWxid)
    const current = accounts.find((a) => a.wxid === currentWxid)

    return (
        <div
            ref={wrapperRef}
            className="fixed z-[300] bg-white border border-hair shadow-lg rounded-lg overflow-hidden"
            style={{
                width: POPOVER_WIDTH,
                top: position?.top ?? -9999,
                left: position?.left ?? -9999,
                visibility: position ? 'visible' : 'hidden',
            }}
            role="dialog"
            aria-label="切换微信账号"
        >
            {/* Current account */}
            {current ? (
                <div className="p-3 bg-accent/5 border-b border-hair">
                    <div className="flex items-center gap-2.5">
                        <img
                            src={avatarDisplaySrc(current.avatar, current.wxid)}
                            alt={current.nickName}
                            className="w-9 h-9 rounded border border-hair"
                        />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-ink truncate">{current.nickName}</div>
                            <div className="text-xs text-muted truncate">{current.wxid}</div>
                        </div>
                        <button
                            type="button"
                            onClick={handleRefresh}
                            disabled={refreshing}
                            className="text-muted hover:text-accent p-1 rounded hover:bg-green-50 transition-colors shrink-0 disabled:opacity-40"
                            title="刷新当前微信资料"
                        >
                            <RefreshCw size={14} className={refreshing ? 'animate-spin' : ''}/>
                        </button>
                        <span className="text-[10px] text-accent bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-full shrink-0">
                            当前
                        </span>
                    </div>
                </div>
            ) : (
                <div className="p-3 border-b border-hair">
                    <div className="flex items-center gap-2.5 text-muted">
                        <div className="w-9 h-9 rounded border border-hair bg-gray-100 flex items-center justify-center">
                            <User size={16}/>
                        </div>
                        <span className="text-sm">未登录</span>
                    </div>
                </div>
            )}

            {/* Online accounts list */}
            {otherOnline.length > 0 && (
                <div className="max-h-[280px] overflow-y-auto">
                    <div className="px-3 pt-2 pb-1">
                        <span className="text-[10px] text-muted uppercase tracking-wider">在线账号</span>
                    </div>
                    {otherOnline.map((acc) => {
                        const unread = accountUnreadCounts[acc.wxid] || 0
                        return (
                            <button
                                key={acc.wxid}
                                type="button"
                                className="w-full px-3 py-2 flex items-center gap-2.5 hover:bg-gray-50 transition-colors text-left"
                                onClick={() => onSwitch(acc.wxid)}
                            >
                                <img
                                    src={avatarDisplaySrc(acc.avatar, acc.wxid)}
                                    alt={acc.nickName}
                                    className="w-8 h-8 rounded border border-hair"
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm text-ink truncate">{acc.nickName}</div>
                                    <div className="text-xs text-muted truncate">{acc.wxid}</div>
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                    {unread > 0 ? (
                                        <span className="min-w-5 h-5 px-1.5 rounded-full bg-red-500 text-[10px] leading-5 text-center text-white font-medium">
                                            {unread > 99 ? '99+' : unread}
                                        </span>
                                    ) : null}
                                    <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0"/>
                                </div>
                            </button>
                        )
                    })}
                </div>
            )}

            {/* No other online accounts */}
            {otherOnline.length === 0 && (
                <div className="px-3 py-4 text-center text-xs text-muted">
                    暂无其他在线账号
                </div>
            )}

            {/* Footer: manage accounts link */}
            <div className="border-t border-hair">
                <button
                    type="button"
                    className="w-full px-3 py-2 flex items-center justify-center gap-1.5 text-xs text-muted hover:text-ink hover:bg-gray-50 transition-colors"
                    onClick={() => {
                        onClose()
                        onManageAccounts()
                    }}
                >
                    <LogIn size={13}/>
                    管理所有实例
                </button>
            </div>
        </div>
    )
}
